import * as React from 'react';
import { ExternalLink, Globe, X } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  addressBarAddressRecipe,
  addressBarPillRecipe,
  addressBarRecipe,
  addressMenuDotRecipe,
  addressMenuRowRecipe,
  browserAddressRecipe,
  browserChromeRecipe,
  browserFrameRecipe,
  mobileAddressPillRecipe,
  phoneFrameRecipe,
  phoneScreenRecipe,
  siteFaviconImageRecipe,
  siteFaviconRecipe,
  tabPreviewRecipe,
  tabRecipe,
  tabStripRecipe,
  tabSwitcherBodyRecipe,
  tabSwitcherCardRecipe,
  tabSwitcherHeadRecipe,
  trafficLightRecipe,
  trafficLightsRecipe,
  type AddressBarAddressRecipeProps,
  type AddressMenuDotRecipeProps,
  type SiteFaviconRecipeProps,
  type TabPreviewRecipeProps,
} from '../recipes/studio.recipe';
import { uiMarkerInternal } from '../recipes/shared';
import { Badge } from './Badge';
import { Icon, IconTile } from './Icon';
import { Text } from './Text';

export interface AddressBarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    AddressBarAddressRecipeProps {
  /** The small line over the address: "Your live site", "Your site address". */
  eyebrow: React.ReactNode;
  /** The address, without its protocol. */
  address: string;
  /** Icon buttons on the right: copy, open, the addresses menu. */
  actions?: React.ReactNode;
  /** Shown instead of the actions when there is nothing to act on yet. */
  hint?: React.ReactNode;
}

/**
 * The address pill above the studio's preview.
 *
 *   <AddressBar eyebrow="Your live site" address="acme.relaytow.com" live
 *     actions={<><Button size="icon-sm" …/><Button size="icon-sm" …/></>} />
 *
 *   <AddressBar eyebrow="Your site address" address={predicted}
 *     hint="Publish to go live" />
 *
 * `live` paints the address as the real thing; `blurred` hides it from an
 * owner who cannot publish yet (and from assistive technology, since a
 * blurred address is not one to read out). The copy and open handlers stay
 * with the page, which owns the clipboard and the toast.
 */
export const AddressBar = React.forwardRef<HTMLDivElement, AddressBarProps>(
  function AddressBar(
    { eyebrow, address, live, blurred, actions, hint, className, ...props },
    ref
  ) {
    return (
      <div ref={ref} className={cn(addressBarRecipe(), className)} {...props}>
        <div className={addressBarPillRecipe()}>
          <IconTile
            icon={Globe}
            size={7}
            shape="circle"
            tone="primary"
            iconTone="primary"
          />
          <div
            className={`${uiMarkerInternal} flex min-w-0 flex-1 flex-col leading-tight`}
          >
            <Text as="span" variant="overline" tone="muted">
              {eyebrow}
            </Text>
            <span
              className={addressBarAddressRecipe({ live, blurred })}
              aria-hidden={blurred || undefined}
            >
              {address}
            </span>
          </div>
          {actions ? (
            <div
              className={`${uiMarkerInternal} flex shrink-0 items-center gap-1`}
            >
              {actions}
            </div>
          ) : hint ? (
            <Badge tone="neutral" size="lg" className="shrink-0">
              {hint}
            </Badge>
          ) : null}
        </div>
      </div>
    );
  }
);

export interface AddressMenuRowProps
  extends React.HTMLAttributes<HTMLDivElement>,
    AddressMenuDotRecipeProps {
  /** The hostname. */
  domain: React.ReactNode;
  /** What the domains page says about it: "Live", "Waiting for DNS". */
  status: React.ReactNode;
  /** The address people are sent to. */
  primary?: boolean;
}

/**
 * One address in the "your addresses" menu.
 *
 *   <DropdownMenuItem onSelect={…}>
 *     <AddressMenuRow domain="acme.com" status="Live" tone="success" primary />
 *   </DropdownMenuItem>
 *
 * A status dot, the domain with its primary mark, the status under it, and
 * the open-in-new-tab glyph at the right. Same wording as the domains page,
 * from the same function, so the two never disagree.
 */
export const AddressMenuRow = React.forwardRef<
  HTMLDivElement,
  AddressMenuRowProps
>(function AddressMenuRow(
  { domain, status, tone, primary, className, ...props },
  ref
) {
  return (
    <div ref={ref} className={cn(addressMenuRowRecipe(), className)} {...props}>
      <span className={addressMenuDotRecipe({ tone })} />
      <span className={`${uiMarkerInternal} flex min-w-0 flex-1 flex-col`}>
        <span className={`${uiMarkerInternal} flex items-center gap-1.5`}>
          <Text as="span" variant="body-sm" truncate>
            {domain}
          </Text>
          {primary ? (
            <Badge tone="neutral" size="sm" className="shrink-0">
              Primary
            </Badge>
          ) : null}
        </span>
        <Text as="span" variant="caption" tone="muted">
          {status}
        </Text>
      </span>
      <Icon
        icon={ExternalLink}
        size="sm"
        tone="muted"
        className="mt-1 shrink-0"
      />
    </div>
  );
});

/** The three window buttons on a browser chrome. Purely decorative. */
function TrafficLights({ size }: { size: 'sm' | 'md' }) {
  return (
    <span className={trafficLightsRecipe({ size })} aria-hidden="true">
      <span className={trafficLightRecipe({ size, tone: 'close' })} />
      <span className={trafficLightRecipe({ size, tone: 'minimise' })} />
      <span className={trafficLightRecipe({ size, tone: 'zoom' })} />
    </span>
  );
}

export interface SiteFaviconProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    Pick<SiteFaviconRecipeProps, 'shape'> {
  /** The icon. Nothing means "no icon yet", and the stand-in shows. */
  src?: string | null;
  /** The site's first letter, shown when there is no icon. */
  initial?: string;
}

/**
 * A site's icon at tab size.
 *
 *   <SiteFavicon src={config.siteIcon} initial={title.charAt(0)} />
 *   <SiteFavicon src={icon} initial="a" shape="circle" />
 *
 * With no icon it shows the site's initial; with no initial either, a globe.
 * Square is how a browser tab draws it, over a checkerboard so a transparent
 * icon stays visible; circle is how a search result does.
 */
export const SiteFavicon = React.forwardRef<HTMLSpanElement, SiteFaviconProps>(
  function SiteFavicon(
    { src, initial, shape = 'square', className, ...props },
    ref
  ) {
    if (src) {
      return (
        <span
          ref={ref}
          className={cn(siteFaviconRecipe({ shape, fill: 'image' }), className)}
          {...props}
        >
          <img src={src} alt="" className={siteFaviconImageRecipe({ shape })} />
        </span>
      );
    }
    if (initial) {
      return (
        <span
          ref={ref}
          className={cn(
            siteFaviconRecipe({ shape, fill: 'initial' }),
            className
          )}
          {...props}
        >
          {initial}
        </span>
      );
    }
    // The same element as the other two states, so a ref or an aria-label
    // survives a site that has neither icon nor name.
    return (
      <span
        ref={ref}
        className={cn(siteFaviconRecipe({ shape, fill: 'initial' }), className)}
        {...props}
      >
        <Icon icon={Globe} size="sm" tone="muted" />
      </span>
    );
  }
);

export interface TabPreviewProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    TabPreviewRecipeProps {
  /** The site icon, if there is one. */
  icon?: string | null;
  /** The site's name, as the tab would show it. */
  title: string;
  /** What the tab says while the site has no name. */
  placeholder?: string;
  /** The hostname in the address field. */
  host: string;
}

/**
 * How the favicon reads in a browser.
 *
 *   <TabPreview variant="desktop" icon={icon} title={name} host={host} />
 *   <TabPreview variant="mobile" icon={icon} title={name} host={host}
 *     placeholder="Your site" />
 *
 * Desktop is a tab strip over a toolbar; mobile is a phone's address pill
 * over a tab-switcher card. The site-icon field shows both, live, so an
 * owner sees the mark at the size it will actually be read.
 */
export const TabPreview = React.forwardRef<HTMLDivElement, TabPreviewProps>(
  function TabPreview(
    {
      variant = 'desktop',
      icon,
      title,
      placeholder = 'Your site',
      host,
      className,
      ...props
    },
    ref
  ) {
    const initial = title.charAt(0);
    if (variant === 'mobile') {
      return (
        <div
          ref={ref}
          className={cn(tabPreviewRecipe({ variant }), className)}
          {...props}
        >
          <span className={mobileAddressPillRecipe()}>
            <SiteFavicon src={icon} initial={initial} />
            <Text as="span" variant="inherit" truncate>
              {host}
            </Text>
          </span>
          <span className={tabSwitcherCardRecipe()}>
            <span className={tabSwitcherHeadRecipe()}>
              <SiteFavicon src={icon} initial={initial} />
              <Text as="span" variant="inherit" truncate>
                {title || placeholder}
              </Text>
            </span>
            <span className={tabSwitcherBodyRecipe()} />
          </span>
        </div>
      );
    }
    return (
      <div
        ref={ref}
        className={cn(tabPreviewRecipe({ variant }), className)}
        {...props}
      >
        <div className={tabStripRecipe()}>
          <span className={tabRecipe()}>
            <SiteFavicon src={icon} initial={initial} />
            <Text as="span" variant="inherit" truncate>
              {title || placeholder}
            </Text>
            <Icon icon={X} size="xs" tone="muted" />
          </span>
        </div>
        <div className={browserChromeRecipe({ size: 'sm' })}>
          <TrafficLights size="sm" />
          <span className={browserAddressRecipe({ size: 'sm' })}>
            <Text as="span" variant="inherit" tone="success">
              🔒
            </Text>
            <Text as="span" variant="inherit" truncate>
              {host}
            </Text>
          </span>
        </div>
      </div>
    );
  }
);

export interface BrowserFrameProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** The address in the chrome's field. */
  address: string;
  /** The site icon beside it; a padlock when there is none. */
  icon?: string | null;
  /** Pixel size of the whole frame. The stage scales it to fit. */
  width: number;
  height: number;
  /** Pixel height of the chrome bar. */
  chromeHeight: number;
  /** The page itself — the studio puts an iframe here. */
  children: React.ReactNode;
}

/**
 * A desktop browser drawn around the site preview.
 *
 *   <BrowserFrame address="acme.relaytow.com" icon={siteIcon}
 *     width={1280} height={800} chromeHeight={44}>
 *     <PreviewFrame>{site}</PreviewFrame>
 *   </BrowserFrame>
 *
 * Window buttons, an address field, then the page. Pixel-sized on purpose:
 * the preview renders the real site at a real desktop width and the stage
 * scales the whole frame down, so the frame cannot be fluid.
 */
export const BrowserFrame = React.forwardRef<HTMLDivElement, BrowserFrameProps>(
  function BrowserFrame(
    {
      address,
      icon,
      width,
      height,
      chromeHeight,
      className,
      style,
      children,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(browserFrameRecipe(), className)}
        style={{ width, height, ...style }}
        {...props}
      >
        <div
          className={browserChromeRecipe({ size: 'md' })}
          style={{ height: chromeHeight }}
        >
          <TrafficLights size="md" />
          <div
            className={`${uiMarkerInternal} flex flex-1 items-stretch justify-center`}
          >
            <span className={browserAddressRecipe({ size: 'md' })}>
              {icon ? (
                <img
                  src={icon}
                  alt=""
                  className={`${uiMarkerInternal} size-4 shrink-0 rounded-[3px] object-contain`}
                />
              ) : (
                <Text as="span" variant="inherit" tone="success">
                  🔒
                </Text>
              )}
              <Text as="span" variant="inherit" truncate>
                {address}
              </Text>
            </span>
          </div>
          {/* Balances the window buttons so the address field sits centred. */}
          <span className={`${uiMarkerInternal} w-[60px] shrink-0`} />
        </div>
        {children}
      </div>
    );
  }
);

export interface PhoneFrameProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Pixel size of the screen. The bezel and corners wrap around it. */
  width: number;
  height: number;
  bezel: number;
  /** Corner radius of the body and of the screen inside it. */
  radius: number;
  screenRadius: number;
  /** The page itself — the studio puts an iframe here. */
  children: React.ReactNode;
}

/**
 * A phone drawn around the site preview.
 *
 *   <PhoneFrame width={390} height={844} bezel={13} radius={58} screenRadius={46}>
 *     <PreviewFrame>{site}</PreviewFrame>
 *   </PhoneFrame>
 *
 * A black body around a rounded screen. Pixel-sized for the same reason the
 * browser frame is: the site renders at a real phone width and the stage
 * scales the result.
 */
export const PhoneFrame = React.forwardRef<HTMLDivElement, PhoneFrameProps>(
  function PhoneFrame(
    {
      width,
      height,
      bezel,
      radius,
      screenRadius,
      className,
      style,
      children,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(phoneFrameRecipe(), className)}
        style={{
          width: width + bezel * 2,
          height: height + bezel * 2,
          borderRadius: radius,
          padding: bezel,
          ...style,
        }}
        {...props}
      >
        <div
          className={phoneScreenRecipe()}
          style={{ width, height, borderRadius: screenRadius }}
        >
          {children}
        </div>
      </div>
    );
  }
);
